import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Stack,
  TablePagination,
  FormControl,
  OutlinedInput,
  InputAdornment,
  IconButton,
  InputLabel
} from '@mui/material';
import FirebaseStorageService from 'firebaseService/firebaseStorage';
import { useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Edit } from '@mui/icons-material';
import SelectDependenceModal from 'views/UsersManagement/SelectDependenceModal';
import DependencyService from '../DependencyService';
import SnackbarAlert from 'ui-component/Alerts/SnackbarAlerts';

export const DependenciesList = ({ dependecies = [], filter }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbarData, setSnackbarData] = useState({
    open: false,
    severity: 'success',
    message: '',
    duration: 3000
  });

  const closeSnackbar = () => {
    setSnackbarData({
      ...snackbarData,
      open: false
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = useMemo(
    () =>
      dependecies
        .filter((dep) => dep.dependence?.toUpperCase().includes(filter.toUpperCase()))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, dependecies, filter]
  );

  const emptyRows =
    page > 0
      ? Math.max(
          0,
          (1 + page) * rowsPerPage - dependecies.filter((dep) => dep.dependence?.toUpperCase().includes(filter.toUpperCase())).length
        )
      : 0;

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tipo</TableCell>
              <TableCell>Dependencia Superior</TableCell>
              <TableCell>Dependencia</TableCell>
              <TableCell>Formato 1</TableCell>
              <TableCell>Formato 2</TableCell>
              <TableCell align="center">Persona de contacto</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((dep) => {
              return <ItemRow key={dep.id} dep={dep} setSnackbarData={setSnackbarData} />;
            })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: 33 * emptyRows
                }}
              >
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={dependecies.filter((dep) => dep.dependence?.toUpperCase().includes(filter.toUpperCase())).length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <SnackbarAlert {...snackbarData} closeSnackbar={closeSnackbar} />
    </>
  );
};

const ItemRow = ({ dep, setSnackbarData }) => {
  const theme = useTheme();
  const [isViewDependenceModal, setViewDependenceModal] = useState(false);

  const AddDependencyParent = async (parent) => {
    try {
      const result = await DependencyService.updateDependency(dep.id, {
        ...dep,
        parent
      });

      const message = result.error ? `Error al actualizar la dependencia: ${result.error.message}` : 'Dependencia actualizada con éxito';

      const severity = result.error ? 'error' : 'success';

      setSnackbarData({
        open: true,
        severity: severity,
        message: message
      });
    } catch (error) {
      setSnackbarData({
        open: true,
        severity: 'error',
        message: 'Ocurrió un error inesperado al actualizar la dependencia.'
      });
    }
  };

  const downloadURL = async (path) => {
    try {
      const url = await FirebaseStorageService.downloadFile(path);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error al descargar el archivo:', error);
    }
  };

  const handleClickShowDep = () => {
    setViewDependenceModal(true);
  };

  const handleMouseDownDep = (event) => {
    event.preventDefault();
  };

  const onClose = () => {
    setViewDependenceModal(false);
  };

  return (
    <>
      <SelectDependenceModal
        noDependencies={[dep.dependence]}
        open={isViewDependenceModal}
        onClose={onClose}
        setDependence={AddDependencyParent}
      />
      <TableRow key={dep.id}>
        <TableCell>{dep.type}</TableCell>
        <TableCell>
          <Tooltip title={dep.parent ?? 'No Tiene'}>
            <FormControl fullWidth sx={{ ...theme.typography.customInput }}>
              <InputLabel htmlFor="label-dependence">Dependencia Superior</InputLabel>
              <OutlinedInput
                fullWidth
                id="label-dependence"
                label="Dependencia"
                name="dependence"
                type="text"
                value={dep.parent}
                // onBlur={handleBlur}
                // onChange={handleChange}
                inputProps={{
                  readOnly: true
                }}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowDep} onMouseDown={handleMouseDownDep} edge="end" size="large">
                      <Edit />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
          </Tooltip>
        </TableCell>
        <TableCell>{dep.dependence}</TableCell>
        <TableCell>
          <Stack direction="row" justifyContent="center">
            <Button disabled={dep.format_1 === ''} size="small" variant="text" onClick={() => downloadURL(dep.format_1)}>
              <Tooltip title="FORMATO 1 - NOMBRAMIENTO DEL REPRESENTANTE INSTITUCIONAL">
                <PictureAsPdfIcon />
              </Tooltip>
            </Button>
          </Stack>
        </TableCell>
        <TableCell>
          <Stack direction="row" justifyContent="center">
            <Button disabled={dep.format_2 === ''} size="small" variant="text" onClick={() => downloadURL(dep.format_2)}>
              <Tooltip title="FORMATO 2 - ACUERDO DE AFILIACIÓN INDIVIDUALIZADO">
                <PictureAsPdfIcon />
              </Tooltip>
            </Button>
          </Stack>
        </TableCell>

        <TableCell>
          <Stack direction="row" justifyContent="center">
            <Button disabled={dep.contact === ''} size="small" variant="text">
              <Tooltip title={`${dep.contact.displayName} - ${dep.contact.phoneNumber}`}>
                <AssignmentIndIcon />
              </Tooltip>
            </Button>
          </Stack>
        </TableCell>
      </TableRow>
    </>
  );
};
